<script setup lang="ts">
    import { Pencil } from 'lucide-vue-next';
    import { Button } from '@/components/ui/button';
    import {
        Dialog,
        DialogTrigger,
        DialogContent,
        DialogHeader,
        DialogTitle,
        DialogClose,
        DialogFooter,
        DialogBody,
    } from '@/components/ui/dialog';
    import { useI18n } from 'vue-i18n';
    import { useForm } from '@inertiajs/vue3';
    import { ref, computed } from 'vue';
    import VoucherAdministrateContent from './VoucherAdministrateContent.vue';
    import { VoucherAssignmentRule } from '@/types';
    import { usePage, router } from '@inertiajs/vue3';
    import { SharedData } from '@/types';
    import { useQueryClient } from '@tanstack/vue-query';
    import { toast } from 'vue-sonner';
    import QueryKey from '@/enums/QueryKey';

    const selectedCompanyId = computed(
        () => usePage<SharedData>().props.auth.user.selected_company_id,
    );

    const { t } = useI18n();
    const queryClient = useQueryClient();

    const isOpen = ref(false);

    const form = useForm({
        cards: [],
        fallback_assignee_ids: [] as string[],
        fallback_employee_ids: [] as number[],
        fallback_user_ids: [] as number[],
        voucher_assignment_rules: [],
    });

    const updateAdministrate = () => {
        form.transform((data) => ({
            ...data,
            voucher_assignment_rules: data.voucher_assignment_rules?.filter(
                (fb: VoucherAssignmentRule) =>
                    !(
                        fb.prompt?.trim() === '' &&
                        (!fb.employee_ids || fb.employee_ids.length === 0) &&
                        (!fb.user_ids || fb.user_ids.length === 0)
                    ),
            ),
        }));
        form.post(
            route('v2.companies.voucher-assignment.update', { company: selectedCompanyId.value }),
            {
                preserveScroll: true,
                onSuccess: () => {
                    form.resetAndClearErrors();

                    isOpen.value = false;

                    queryClient.invalidateQueries({
                        queryKey: [QueryKey.COMPANY_VOUCHER_ASSIGNMENT, selectedCompanyId.value],
                    });

                    toast.success(t('vouchers.administrate.success'));
                },
                onError: (error: any) => {
                    toast.error(error?.error || t('vouchers.administrate.error'));
                },
            },
        );
    };
</script>

<template>
    <Dialog v-model:open="isOpen">
        <DialogTrigger as-child>
            <Button
                variant="ghost_warning"
                width="narrow"
                class="self-start"
                size="xs"
            >
                {{ t('vouchers.administrate.title') }}
                <Pencil class="text-warning size-3" />
            </Button>
        </DialogTrigger>
        <DialogContent class="sm:max-w-3xl">
            <form
                @submit.prevent="updateAdministrate"
                class="flex max-h-full flex-col overflow-hidden"
            >
                <DialogHeader>
                    <DialogTitle>{{ t('vouchers.administrate.title') }}</DialogTitle>
                </DialogHeader>

                <DialogBody class="space-y-5">
                    <VoucherAdministrateContent :form="form" />
                </DialogBody>

                <DialogFooter class="gap-2 border-t sm:justify-center">
                    <DialogClose as-child>
                        <Button
                            variant="outline"
                            size="sm"
                            @click="isOpen = false"
                        >
                            {{ t('forms.cancel') }}
                        </Button>
                    </DialogClose>

                    <Button
                        :disabled="form.processing"
                        :loading="form.processing"
                        size="sm"
                    >
                        {{ t('forms.save') }}
                    </Button>
                </DialogFooter>
            </form>
        </DialogContent>
    </Dialog>
</template>
